import useCalendar from "hooks/useCalendar";
import { Divider, LinearProgress, Stack, Typography } from "@material-ui/core";
import { CalendarTasksRow } from "components/_dashboard/calendar/CalendarTasksRow";
import { useTranslation } from "react-i18next";
import { sortCalendarEvents } from "utils/calendar/filters";
import type { CalendarEvent, RealEstateContact } from "../../../../../@types/sharedSchema";

type ContactTableNestedRowEventsProps = {
  contact: RealEstateContact;
};

export function ContactTableNestedRowEvents({
  contact
}: ContactTableNestedRowEventsProps): JSX.Element {
  const { t: translate } = useTranslation();

  const { events, isFetchingEvents } = useCalendar();

  // The document id IS the contact's phone (see `contactRekey.ts`). `callerPhoneNumber`
  // is a legacy mirror: absent on ~90% of documents and, where present, sometimes a
  // local-format string that agrees with nothing.
  const contactPhone = contact.id;

  const contactEvents = getContactEvents(contactPhone, events);

  return (
    <>
      <Divider />
      <Stack gap={2}>
        <Typography variant="h6">{translate("contactsPage.control.calendarEvents")}</Typography>
        {isFetchingEvents ? (
          <LinearProgress />
        ) : contactEvents.length ? (
          <Stack>
            {contactEvents.map((event) => (
              <CalendarTasksRow event={event} currentTab="upcoming" key={event.id} />
            ))}
          </Stack>
        ) : (
          <Typography variant="body1">
            {translate("contactsPage.error.noCalendarEventsFound")}
          </Typography>
        )}
      </Stack>
    </>
  );
}

export function getContactEvents(
  contactPhone: string,
  events: CalendarEvent[] | null,
  sortEvents: boolean = true
): CalendarEvent[] {
  if (!events) return [];

  const contactEvents = events.filter(({ attendees }) =>
    attendees?.some(
      ({ phoneNumberInternational }) => phoneNumberInternational === contactPhone
    )
  );

  if (!sortEvents) return contactEvents;

  const sortedContactEvents = sortCalendarEvents(contactEvents);

  return sortedContactEvents;
}
